import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Pagination } from 'components/Pagination/Pagination'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { FilterEnum, IFilter } from 'components/Filter/SingleFilter'
import useQueryPagination from 'hooks/useQueryPagination'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { Flex, H1WithBack, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText } from 'styling/Typography'
import { formatDateForDisplay, getParams, handlePagination } from 'utils/helpers'
import {
  clearFuelDeliveryRequests,
  getFuelDeliveryRequests,
} from 'features/fuelManagement/fuelManagementSlice'
import { IFuelDeliveryRequestResponse } from 'types/api/fuelDeliveryRequestsApi.interface'
import { FuelDeliveryRequestStatus } from 'types/fuelEnums.interface'
import {
  fuelDeliveryFuelTypeLabel,
  fuelDeliveryRequestStatusLabel,
  fuelDeliveryRequestStatuses,
} from '../fuelData'
import { FuelDeliveryRequestDetailModal } from './FuelDeliveryRequestDetailModal'
import { FuelDeliveryRequestFormModal } from './FuelDeliveryRequestFormModal'

const statusTone = (status: number): any => {
  if (status === FuelDeliveryRequestStatus.Cancelled) return 'danger'
  if (status === FuelDeliveryRequestStatus.Fulfilled) return 'success'
  if (
    status === FuelDeliveryRequestStatus.PartiallyReceived ||
    status === FuelDeliveryRequestStatus.PartiallyFulfilled
  ) {
    return 'accent'
  }
  if (status === FuelDeliveryRequestStatus.Received) return 'info'
  return 'neutral'
}

export const FuelDeliveryRequests = () => {
  const navigate = useNavigate()
  const [params, setParams] = useState<any>(getParams({ location }))
  const [createOpen, setCreateOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const { deliveryRequests } = useSelector((store: RootState) => store.fuelManagement)

  useQueryPagination({
    fetchFunctions: [getFuelDeliveryRequests],
    cleanUpFunctions: [clearFuelDeliveryRequests],
    params,
  })

  const rows = deliveryRequests?.data ?? []

  const filters: IFilter[] = [
    {
      type: FilterEnum.FIXED_LIST,
      name: 'Status',
      state: null,
      propName: 'status',
      sd: true,
      single: true,
      filterOptions: fuelDeliveryRequestStatuses.map((s) => ({
        name: s.label,
        id: s.value.toString(),
      })),
    },
  ]

  const columns: IHonestTableColumn<IFuelDeliveryRequestResponse>[] = [
    {
      id: 'id',
      header: 'ID',
      sticky: true,
      cell: (item) => <BodyText weight='600'>#{item.id}</BodyText>,
    },
    {
      id: 'createdAt',
      header: 'Created',
      cell: (item) => formatDateForDisplay(item.createdAt),
    },
    {
      id: 'department',
      header: 'Department',
      cell: (item) => item.department?.name || '—',
    },
    {
      id: 'supplier',
      header: 'Supplier',
      cell: (item) => item.supplier?.name || '—',
    },
    {
      id: 'lines',
      header: 'Lines',
      cell: (item) =>
        (item.lines || [])
          .map(
            (line) =>
              `${fuelDeliveryFuelTypeLabel(line.fuelType)} ${line.quantity}${line.unit?.name ? ` ${line.unit.name}` : ''}`,
          )
          .join(' · ') || '—',
    },
    {
      id: 'createdBy',
      header: 'Created by',
      cell: (item) =>
        item.createdBy
          ? `${item.createdBy.firstName} ${item.createdBy.lastName}`
          : '—',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item) => (
        <StatusPill status={statusTone(item.status)}>
          {fuelDeliveryRequestStatusLabel(item.status)}
        </StatusPill>
      ),
    },
  ]

  return (
    <>
      <NavBar
        filterOptions={filters}
        enableTemporaryManualClosing
        setParams={setParams}
        params={params}
      />
      <PageWrapper>
        <Flex between mb='16' center>
          <Flex column gap='0.25rem'>
            <H1WithBack
              title='Delivery Requests'
              arrow
              onClick={() => navigate('/fuel/dashboard')}
            />
            <OtherText grey>
              Request fuel and auto-create a matching procurement purchase request
            </OtherText>
          </Flex>
          <Button variant='primary' size='sm' onClick={() => setCreateOpen(true)}>
            New request
          </Button>
        </Flex>
        {!rows.length ? (
          <NoItems title='Fuel Delivery Requests' />
        ) : (
          <HonestTable
            columns={columns}
            data={rows}
            keyField='id'
            onClick={(item) => setSelectedId(item.id)}
          />
        )}
      </PageWrapper>
      <PageFooter>
        <Pagination
          setPage={(page) => handlePagination(page, setParams)}
          pageCount={Math.ceil(deliveryRequests.totalPages)}
          forcePage={(params.page ? params.page : 1) - 1}
        />
      </PageFooter>
      {createOpen && (
        <FuelDeliveryRequestFormModal closeModal={() => setCreateOpen(false)} />
      )}
      {selectedId != null && (
        <FuelDeliveryRequestDetailModal
          requestId={selectedId}
          closeModal={() => setSelectedId(null)}
        />
      )}
    </>
  )
}
